<template>
  <q-card flat bordered>
    <!-- Toolbar -->
    <q-card-section class="row items-center q-col-gutter-sm q-py-sm">
      <div class="col-12 col-md-4">
        <q-input
          :model-value="queueManager.searchQuery.value"
          dense
          clearable
          debounce="0"
          :placeholder="t('table.searchPlaceholder')"
          @update:model-value="(value) => queueManager.setSearchQuery(String(value ?? ''))">
          <template #prepend>
            <q-icon :name="symRoundedSearch" />
          </template>
        </q-input>
      </div>
      <div class="col-6 col-md-2">
        <q-select
          :model-value="queueManager.priorityFilter.value"
          dense
          emit-value
          map-options
          :options="priorityOptions"
          :label="t('table.columns.priority')"
          @update:model-value="(value) => queueManager.setPriorityFilter(value)" />
      </div>
      <div class="col-auto">
        <q-btn
          v-if="queueManager.hasActiveFilters.value"
          flat
          dense
          color="grey-6"
          :icon="symRoundedFilterAltOff"
          :label="t('queue.clearFilters')"
          @click="queueManager.clearFilters()" />
      </div>
      <q-space />
      <!-- Bulk actions -->
      <div
        v-if="selectedRows.length > 0"
        class="col-auto row items-center q-gutter-xs">
        <span class="text-caption text-grey-6">{{ t('table.selectedCount', { count: selectedRows.length }) }}</span>
        <q-btn
          v-if="selectedRetryable.length > 0"
          dense
          flat
          color="warning"
          :icon="symRoundedRefresh"
          :label="t('queue.retrySelected')"
          @click="onBulkRetry" />
        <q-btn
          v-if="selectedActive.length > 0"
          dense
          flat
          color="negative"
          :icon="symRoundedStopCircle"
          :label="t('queue.cancelSelected')"
          @click="onBulkCancel" />
        <q-btn
          v-if="selectedFinal.length > 0"
          dense
          flat
          color="negative"
          :icon="symRoundedDelete"
          :label="t('queue.deleteSelected')"
          @click="onBulkDelete" />
      </div>
    </q-card-section>

    <q-separator />

    <q-table
      v-model:selected="selectedRows"
      v-model:pagination="pagination"
      flat
      row-key="id"
      selection="multiple"
      binary-state-sort
      :grid="$q.screen.lt.md"
      :rows="rows"
      :columns="columns"
      :loading="queueManager.isLoading.value"
      :rows-per-page-options="[10, 20, 50, 100]"
      :rows-per-page-label="t('table.recordsPerPage')"
      :pagination-label="paginationLabel"
      @request="onRequest"
      @row-click="onRowClick">
      <!-- Mobile: each queue item as a tappable card -->
      <template #item="itemProps">
        <div class="col-12 q-py-xs">
          <q-card
            flat
            bordered
            class="queue-card cursor-pointer"
            @click="openWorkbench(itemProps.row)">
            <q-card-section class="q-pa-sm">
              <div class="row items-center no-wrap q-gutter-sm">
                <q-checkbox
                  :model-value="itemProps.selected"
                  dense
                  @update:model-value="(value) => (itemProps.selected = value)"
                  @click.stop />
                <q-icon
                  :name="fileIconFor(itemProps.row.documents?.[0]?.mime_type ?? itemProps.row.mime_type)"
                  size="24px"
                  color="grey-6" />
                <div
                  class="column col"
                  style="min-width: 0">
                  <span class="text-weight-medium ellipsis">
                    {{ itemProps.row.original_filename || t('queue.unknownFilename') }}
                  </span>
                  <span class="text-caption text-grey-6">
                    {{ timeAgo(itemProps.row.created_at) }}
                    <template v-if="formatSize(itemProps.row.documents?.[0]?.size ?? itemProps.row.file_size)">
                      • {{ formatSize(itemProps.row.documents?.[0]?.size ?? itemProps.row.file_size) }}
                    </template>
                  </span>
                </div>
                <StatusPill
                  :color="STATUS_COLOR[itemProps.row.status as AiQueueStatus] ?? 'default'"
                  :label="t(`status.${itemProps.row.status}`)" />
              </div>

              <div
                v-if="isActiveStatus(itemProps.row)"
                class="q-mt-sm">
                <div
                  v-if="itemProps.row.queue_step"
                  class="text-caption text-grey-6 q-mb-xs">
                  {{ t(`steps.${itemProps.row.queue_step}`) }}
                </div>
                <q-linear-progress
                  :value="stepProgress(itemProps.row)"
                  :indeterminate="itemProps.row.status === 'processing'"
                  color="info"
                  size="3px"
                  rounded />
              </div>

              <div
                v-if="itemProps.row.status === 'failed' && errorSummary(itemProps.row)"
                class="text-caption text-negative q-mt-sm">
                {{ errorSummary(itemProps.row) }}
              </div>

              <div
                v-if="itemProps.row.generated_records?.length"
                class="row items-center q-gutter-xs q-mt-sm"
                @click.stop>
                <CrudPreview
                  v-for="record in itemProps.row.generated_records.slice(0, 2)"
                  :key="`${record.model}:${record.id}`"
                  :model-name="record.model"
                  :model-id="record.id"
                  size="sm" />
                <span
                  v-if="itemProps.row.generated_records.length > 2"
                  class="text-caption text-grey-6">
                  +{{ itemProps.row.generated_records.length - 2 }}
                </span>
              </div>
            </q-card-section>

            <q-separator />

            <q-card-actions
              class="q-px-sm q-py-xs"
              @click.stop>
              <StatusPill
                v-if="itemProps.row.priority !== 'normal'"
                :color="priorityColor(itemProps.row.priority)"
                :label="t(`priority.${itemProps.row.priority}`)" />
              <span
                v-else-if="topSuggestion(itemProps.row) && !itemProps.row.confirmed_model_type"
                class="text-caption text-grey-6">
                {{ modelLabel(topSuggestion(itemProps.row)!.model) }} ({{ Math.round(topSuggestion(itemProps.row)!.confidence * 100) }}%)
              </span>
              <span
                v-else-if="itemProps.row.confirmed_model_type"
                class="text-caption text-grey-6">
                {{ modelLabel(itemProps.row.confirmed_model_type) }}
              </span>
              <q-space />
              <q-btn
                flat
                round
                dense
                size="sm"
                :icon="symRoundedVisibility"
                @click="openWorkbench(itemProps.row)">
                <q-tooltip>{{ t('queue.viewDetails') }}</q-tooltip>
              </q-btn>
              <q-btn
                v-if="needsModelConfirmation(itemProps.row)"
                flat
                round
                dense
                size="sm"
                color="accent"
                :icon="symRoundedFactCheck"
                @click="onConfirmModel(itemProps.row)">
                <q-tooltip>{{ t('modelSelection.title') }}</q-tooltip>
              </q-btn>
              <q-btn
                v-if="itemProps.row.status === 'failed'"
                flat
                round
                dense
                size="sm"
                color="warning"
                :icon="symRoundedRefresh"
                @click="onRetry(itemProps.row)">
                <q-tooltip>{{ t('queue.retryProcessing') }}</q-tooltip>
              </q-btn>
              <q-btn
                v-if="isActiveStatus(itemProps.row)"
                flat
                round
                dense
                size="sm"
                color="negative"
                :icon="symRoundedStopCircle"
                @click="onCancel(itemProps.row)">
                <q-tooltip>{{ t('queue.cancelProcessing') }}</q-tooltip>
              </q-btn>
              <q-btn
                v-if="isFinalStatus(itemProps.row)"
                flat
                round
                dense
                size="sm"
                color="grey-6"
                :icon="symRoundedDelete"
                @click="onDelete(itemProps.row)">
                <q-tooltip>{{ t('actions.delete') }}</q-tooltip>
              </q-btn>
            </q-card-actions>
          </q-card>
        </div>
      </template>

      <!-- Document -->
      <template #body-cell-document="cellProps">
        <q-td :props="cellProps">
          <div class="row items-center no-wrap q-gutter-sm">
            <q-icon
              :name="fileIconFor(cellProps.row.documents?.[0]?.mime_type ?? cellProps.row.mime_type)"
              size="22px"
              color="grey-6" />
            <div class="column" style="min-width: 0">
              <span class="text-weight-medium ellipsis" style="max-width: 260px">
                {{ cellProps.row.original_filename || t('queue.unknownFilename') }}
              </span>
              <span class="text-caption text-grey-6">
                {{ formatSize(cellProps.row.documents?.[0]?.size ?? cellProps.row.file_size) }}
                <template v-if="cellProps.row.retry_count > 0">
                  • {{ t('table.attempt', { count: cellProps.row.retry_count, max: cellProps.row.max_retries }) }}
                </template>
              </span>
            </div>
          </div>
        </q-td>
      </template>

      <!-- Status -->
      <template #body-cell-status="cellProps">
        <q-td :props="cellProps">
          <div class="column q-gutter-xs" style="min-width: 150px">
            <div>
              <StatusPill
                :color="STATUS_COLOR[cellProps.row.status as AiQueueStatus] ?? 'default'"
                :label="t(`status.${cellProps.row.status}`)" />
            </div>
            <template v-if="isActiveStatus(cellProps.row)">
              <span
                v-if="cellProps.row.queue_step"
                class="text-caption text-grey-6">
                {{ t(`steps.${cellProps.row.queue_step}`) }}
              </span>
              <q-linear-progress
                :value="stepProgress(cellProps.row)"
                :indeterminate="cellProps.row.status === 'processing'"
                color="info"
                size="3px"
                rounded />
            </template>
            <q-tooltip
              v-if="cellProps.row.status === 'failed' && errorSummary(cellProps.row)"
              max-width="360px">
              {{ errorSummary(cellProps.row) }}
            </q-tooltip>
          </div>
        </q-td>
      </template>

      <!-- Priority -->
      <template #body-cell-priority="cellProps">
        <q-td :props="cellProps">
          <StatusPill
            :color="priorityColor(cellProps.row.priority)"
            :label="t(`priority.${cellProps.row.priority}`)" />
        </q-td>
      </template>

      <!-- AI model -->
      <template #body-cell-ai="cellProps">
        <q-td :props="cellProps">
          <template v-if="cellProps.row.confirmed_model_type">
            <span class="text-weight-medium">{{ modelLabel(cellProps.row.confirmed_model_type) }}</span>
          </template>
          <template v-else-if="topSuggestion(cellProps.row)">
            <div class="column">
              <span>{{ modelLabel(topSuggestion(cellProps.row)!.model) }} ({{ Math.round(topSuggestion(cellProps.row)!.confidence * 100) }}%)</span>
              <span class="text-caption text-grey-6">{{ t('table.suggestion') }}</span>
            </div>
          </template>
          <span v-else class="text-grey-6">—</span>
        </q-td>
      </template>

      <!-- Generated records -->
      <template #body-cell-result="cellProps">
        <q-td
          :props="cellProps"
          @click.stop>
          <div
            v-if="cellProps.row.generated_records?.length"
            class="row items-center q-gutter-xs no-wrap">
            <CrudPreview
              v-for="record in cellProps.row.generated_records.slice(0, 2)"
              :key="`${record.model}:${record.id}`"
              :model-name="record.model"
              :model-id="record.id"
              size="sm" />
            <span
              v-if="cellProps.row.generated_records.length > 2"
              class="text-caption text-grey-6">
              +{{ cellProps.row.generated_records.length - 2 }}
            </span>
          </div>
          <span
            v-else
            class="text-caption text-grey-6">
            {{ cellProps.row.status === 'completed' ? '—' : t('table.noResult') }}
          </span>
        </q-td>
      </template>

      <!-- Created -->
      <template #body-cell-created="cellProps">
        <q-td :props="cellProps">
          <span class="text-no-wrap">{{ timeAgo(cellProps.row.created_at) }}</span>
          <q-tooltip>{{ formatDate(cellProps.row.created_at, true) }}</q-tooltip>
        </q-td>
      </template>

      <!-- Actions -->
      <template #body-cell-actions="cellProps">
        <q-td
          :props="cellProps"
          @click.stop>
          <div class="row items-center no-wrap justify-end">
            <q-btn
              flat
              round
              dense
              size="sm"
              :icon="symRoundedVisibility"
              @click="openWorkbench(cellProps.row)">
              <q-tooltip>{{ t('queue.viewDetails') }}</q-tooltip>
            </q-btn>
            <q-btn
              v-if="needsModelConfirmation(cellProps.row)"
              flat
              round
              dense
              size="sm"
              color="accent"
              :icon="symRoundedFactCheck"
              @click="onConfirmModel(cellProps.row)">
              <q-tooltip>{{ t('modelSelection.title') }}</q-tooltip>
            </q-btn>
            <q-btn
              v-if="cellProps.row.status === 'failed'"
              flat
              round
              dense
              size="sm"
              color="warning"
              :icon="symRoundedRefresh"
              @click="onRetry(cellProps.row)">
              <q-tooltip>{{ t('queue.retryProcessing') }}</q-tooltip>
            </q-btn>
            <q-btn
              v-if="isActiveStatus(cellProps.row)"
              flat
              round
              dense
              size="sm"
              color="negative"
              :icon="symRoundedStopCircle"
              @click="onCancel(cellProps.row)">
              <q-tooltip>{{ t('queue.cancelProcessing') }}</q-tooltip>
            </q-btn>
            <q-btn
              v-if="isFinalStatus(cellProps.row)"
              flat
              round
              dense
              size="sm"
              color="grey-6"
              :icon="symRoundedDelete"
              @click="onDelete(cellProps.row)">
              <q-tooltip>{{ t('actions.delete') }}</q-tooltip>
            </q-btn>
          </div>
        </q-td>
      </template>

      <!-- Empty state -->
      <template #no-data>
        <div class="column items-center q-pa-xl full-width text-center">
          <q-icon
            :name="queueManager.hasActiveFilters.value ? symRoundedFilterAltOff : symRoundedCloudUpload"
            size="42px"
            color="grey-6" />
          <div class="text-subtitle1 q-mt-sm">
            {{ queueManager.hasActiveFilters.value ? t('table.emptyFiltered.title') : t('table.empty.title') }}
          </div>
          <div class="text-caption text-grey-6">
            {{ queueManager.hasActiveFilters.value ? t('table.emptyFiltered.hint') : t('table.empty.hint') }}
          </div>
        </div>
      </template>
    </q-table>
  </q-card>
</template>

<script setup lang="ts">
import {
  symRoundedCloudUpload,
  symRoundedDelete,
  symRoundedFactCheck,
  symRoundedFilterAltOff,
  symRoundedRefresh,
  symRoundedSearch,
  symRoundedStopCircle,
  symRoundedVisibility,
} from '@quasar/extras/material-symbols-rounded'
import type { QTableColumn } from 'quasar'
import { useQuasar } from 'quasar'
import useDate from 'src/CrudManager/Core/composables/useDate'
import { useCompositeI18n } from 'src/CrudManager/Core/composables/useCompositeI18n'
import StatusPill from 'src/CrudManager/Core/composites/Ui/StatusPill/StatusPill.vue'
import { computed, inject, ref, watch } from 'vue'
import { queueManagerKey } from '../composables/queueManagerKey'
import { openModelSelectorDialog } from '../composables/useModelSelectorDialog'
import { openQueueWorkbenchDialog } from '../composables/useQueueDetailsDialog'
import type { AiQueueItem, ModelSuggestion } from '../types'
import { ACTIVE_STATUSES, AiQueueStatus, getModelSuggestions, STEP_PROGRESS } from '../types'
import { fileIconFor, STATUS_COLOR } from '../utils/statusMaps'

/**
 * Server-driven processing queue table: pagination, status/priority filters
 * and search all hit the backend. Row click opens the document workbench;
 * generated records open directly in CrudPreview from the list.
 */
const queueManager = inject(queueManagerKey)!

const { t } = useCompositeI18n('core.AiDocumentProcessor')
const { formatDate, timeAgo } = useDate()
const $q = useQuasar()

const rows = computed(() => [...queueManager.queueItems.value] as AiQueueItem[])

const selectedRows = ref<AiQueueItem[]>([])

const pagination = ref({
  page: 1,
  rowsPerPage: 20,
  rowsNumber: 0,
})

watch(
  [queueManager.currentPage, queueManager.perPage, queueManager.totalItems],
  ([page, rowsPerPage, rowsNumber]) => {
    pagination.value = { page, rowsPerPage, rowsNumber }
  },
  { immediate: true },
)

// Drop selections that fell off the current page after a refresh
watch(rows, (currentRows) => {
  const ids = new Set(currentRows.map((row) => row.id))
  selectedRows.value = selectedRows.value.filter((row) => ids.has(row.id))
})

const columns = computed<QTableColumn[]>(() => [
  { name: 'document', label: t('table.columns.document'), field: 'original_filename', align: 'left', sortable: false },
  { name: 'status', label: t('table.columns.status'), field: 'status', align: 'left', sortable: false },
  { name: 'priority', label: t('table.columns.priority'), field: 'priority', align: 'left', sortable: false },
  { name: 'ai', label: t('table.columns.ai'), field: 'confirmed_model_type', align: 'left', sortable: false },
  { name: 'result', label: t('table.columns.result'), field: 'generated_records', align: 'left', sortable: false },
  { name: 'created', label: t('table.columns.created'), field: 'created_at', align: 'left', sortable: false },
  { name: 'actions', label: t('table.columns.actions'), field: 'id', align: 'right', sortable: false },
])

const priorityOptions = computed(() => [
  { label: t('priority.all'), value: 'all' },
  { label: t('priority.high'), value: 'high' },
  { label: t('priority.normal'), value: 'normal' },
  { label: t('priority.low'), value: 'low' },
])

const paginationLabel = (firstRowIndex: number, endRowIndex: number, totalRowsNumber: number): string =>
  t('table.paginationOf', { from: firstRowIndex, to: endRowIndex, total: totalRowsNumber })

// ── Row helpers ──────────────────────────────────────────────────────────

const isActiveStatus = (row: AiQueueItem): boolean => ACTIVE_STATUSES.includes(row.status)

const isFinalStatus = (row: AiQueueItem): boolean => !isActiveStatus(row)

const needsModelConfirmation = (row: AiQueueItem): boolean =>
  row.status === AiQueueStatus.AwaitingUserAction && row.queue_step === 'awaiting_model_confirmation'

const stepProgress = (row: AiQueueItem): number => (row.queue_step ? (STEP_PROGRESS[row.queue_step] ?? 0) / 100 : 0)

const topSuggestion = (row: AiQueueItem): ModelSuggestion | null => {
  const suggestions = getModelSuggestions(row)
  if (!suggestions?.length) return null
  return [...suggestions].sort((a, b) => b.confidence - a.confidence)[0] ?? null
}

const modelLabel = (modelType: string): string => {
  const key = `records.models.${modelType}`
  const label = t(key)
  return label === `core.AiDocumentProcessor.${key}` ? modelType : label
}

const priorityColor = (priority: string): string => {
  if (priority === 'high') return 'orange'
  if (priority === 'low') return 'muted'
  return 'default'
}

const errorSummary = (row: AiQueueItem): string | null => {
  const errors = row.processing_errors
  if (!errors) return null
  return (errors.message as string) || (errors.error as string) || JSON.stringify(errors)
}

const formatSize = (bytes: number | null | undefined): string => {
  if (!bytes) return ''
  const units = [t('upload.bytes'), t('upload.kb'), t('upload.mb'), t('upload.gb')]
  const index = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${parseFloat((bytes / Math.pow(1024, index)).toFixed(1))} ${units[index]}`
}

// ── Data + navigation ────────────────────────────────────────────────────

const onRequest = (requestProps: { pagination: { page: number; rowsPerPage: number } }): void => {
  void queueManager.loadQueue(requestProps.pagination.page, requestProps.pagination.rowsPerPage)
}

const onRowClick = (_event: Event, row: AiQueueItem): void => {
  openWorkbench(row)
}

const openWorkbench = (row: AiQueueItem): void => {
  openQueueWorkbenchDialog(row, () => {
    void queueManager.refresh()
  })
}

// ── Actions ──────────────────────────────────────────────────────────────

const onConfirmModel = (row: AiQueueItem): void => {
  void openModelSelectorDialog(row).then(() => queueManager.refresh())
}

const onRetry = (row: AiQueueItem): void => {
  void queueManager.retryProcessing(row.id)
}

const onCancel = (row: AiQueueItem): void => {
  $q.dialog({
    title: t('confirms.cancelTitle'),
    message: t('confirms.cancelMessage', { name: row.original_filename || row.id }),
    cancel: { flat: true, label: t('actions.cancel') },
    ok: { color: 'negative', label: t('actions.confirm') },
  }).onOk(() => {
    void queueManager.cancelProcessing(row.id)
  })
}

const onDelete = (row: AiQueueItem): void => {
  $q.dialog({
    title: t('confirms.deleteTitle'),
    message: t('confirms.deleteMessage', { name: row.original_filename || row.id }),
    cancel: { flat: true, label: t('actions.cancel') },
    ok: { color: 'negative', label: t('actions.delete') },
  }).onOk(() => {
    void queueManager.deleteQueue(row.id)
  })
}

// ── Bulk actions ─────────────────────────────────────────────────────────

const selectedRetryable = computed(() => selectedRows.value.filter((row) => row.status === AiQueueStatus.Failed))
const selectedActive = computed(() => selectedRows.value.filter((row) => isActiveStatus(row)))
const selectedFinal = computed(() => selectedRows.value.filter((row) => isFinalStatus(row)))

const onBulkRetry = (): void => {
  void queueManager.bulkRetry(selectedRetryable.value.map((row) => row.id)).then(() => {
    selectedRows.value = []
  })
}

const onBulkCancel = (): void => {
  $q.dialog({
    title: t('confirms.bulkCancelTitle'),
    message: t('confirms.bulkCancelMessage', { count: selectedActive.value.length }),
    cancel: { flat: true, label: t('actions.cancel') },
    ok: { color: 'negative', label: t('actions.confirm') },
  }).onOk(() => {
    void queueManager.bulkCancel(selectedActive.value.map((row) => row.id)).then(() => {
      selectedRows.value = []
    })
  })
}

const onBulkDelete = (): void => {
  $q.dialog({
    title: t('confirms.bulkDeleteTitle'),
    message: t('confirms.bulkDeleteMessage', { count: selectedFinal.value.length }),
    cancel: { flat: true, label: t('actions.cancel') },
    ok: { color: 'negative', label: t('actions.delete') },
  }).onOk(() => {
    void queueManager.bulkDelete(selectedFinal.value.map((row) => row.id)).then(() => {
      selectedRows.value = []
    })
  })
}
</script>
